import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { BackButton, HomeButton } from '../components/NavButtons';
import { ArrowLeft, GridFour, Check, X, Wrench, Circle, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  available: { color: 'bg-emerald-500', border: 'border-emerald-600', text: 'text-white', label: 'Available', hoverColor: 'hover:bg-emerald-400' },
  occupied: { color: 'bg-red-500', border: 'border-red-600', text: 'text-white', label: 'Occupied', hoverColor: 'hover:bg-red-400' },
  reserved: { color: 'bg-amber-400', border: 'border-amber-500', text: 'text-white', label: 'Reserved', hoverColor: 'hover:bg-amber-300' },
  maintenance: { color: 'bg-slate-400', border: 'border-slate-500', text: 'text-white', label: 'Maintenance', hoverColor: 'hover:bg-slate-300' },
};

const UNIT_TYPES = [
  { value: 'slot', label: 'Parking Slot' },
  { value: 'seat', label: 'Seat' },
  { value: 'room', label: 'Room' },
  { value: 'desk', label: 'Desk' },
  { value: 'bay', label: 'Bay / Station' },
  { value: 'court', label: 'Court / Field' },
  { value: 'unit', label: 'Unit' },
  { value: 'table', label: 'Table' },
  { value: 'pod', label: 'Pod / Cabin' },
];

const SpaceManager = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [units, setUnits] = useState([]);
  const [grid, setGrid] = useState({});
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Setup form
  const [showSetup, setShowSetup] = useState(false);
  const [setupRows, setSetupRows] = useState(4);
  const [setupCols, setSetupCols] = useState(6);
  const [setupType, setSetupType] = useState('slot');

  // Selection for bulk actions
  const [selectedUnits, setSelectedUnits] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('occupied');
  const [paintMode, setPaintMode] = useState(null); // null, 'available', 'occupied', 'reserved', 'maintenance'

  const fetchData = useCallback(async () => {
    try {
      const [listingRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/listings/${listingId}`),
        axios.get(`${API_URL}/api/spaces/${listingId}`).catch(() => ({ data: { units: [], grid: {}, summary: {} } }))
      ]);
      setListing(listingRes.data);
      setUnits(unitsRes.data.units || []);
      setGrid(unitsRes.data.grid || {});
      setSummary(unitsRes.data.summary || {});
      if (!unitsRes.data.units?.length) setShowSetup(true);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSetup = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/spaces/${listingId}/setup`, {
        rows: setupRows, cols: setupCols, unit_type: setupType
      });
      setShowSetup(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Setup failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleUnit = async (unit) => {
    if (paintMode) {
      setSaving(true);
      try {
        await axios.put(`${API_URL}/api/spaces/${listingId}/units/${unit.unit_id}`, { status: paintMode });
        setUnits(prev => prev.map(u => u.unit_id === unit.unit_id ? { ...u, status: paintMode } : u));
        updateSummaryLocal();
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Default: cycle available -> occupied -> available
    const newStatus = unit.status === 'available' ? 'occupied' : 'available';
    try {
      await axios.put(`${API_URL}/api/spaces/${listingId}/units/${unit.unit_id}`, { status: newStatus });
      setUnits(prev => prev.map(u => u.unit_id === unit.unit_id ? { ...u, status: newStatus } : u));
      updateSummaryLocal();
    } catch (err) {
      console.error(err);
    }
  };

  const updateSummaryLocal = () => {
    setTimeout(() => {
      setUnits(prev => {
        const s = { total: prev.length, available: 0, occupied: 0, reserved: 0, maintenance: 0 };
        prev.forEach(u => { s[u.status] = (s[u.status] || 0) + 1; });
        setSummary(s);
        return prev;
      });
    }, 100);
  };

  const handleBulkUpdate = async () => {
    if (selectedUnits.size === 0) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/spaces/${listingId}/bulk-update`, {
        unit_ids: Array.from(selectedUnits), status: bulkStatus
      });
      setUnits(prev => prev.map(u => selectedUnits.has(u.unit_id) ? { ...u, status: bulkStatus } : u));
      setSelectedUnits(new Set());
      updateSummaryLocal();
    } catch (err) {
      alert('Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (unitId) => {
    setSelectedUnits(prev => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  // Group units by row
  const rows = {};
  units.forEach(u => {
    if (!rows[u.row]) rows[u.row] = [];
    rows[u.row].push(u);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-4">
          <BackButton />
          <HomeButton />
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-slate-900 truncate" data-testid="page-title">Manage Spaces</h1>
            <p className="text-sm text-slate-500 truncate">{listing?.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowSetup(true)} className="rounded-xl text-sm" data-testid="reconfigure-btn">
              <GridFour size={18} className="mr-1.5" /> Reconfigure
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Setup Modal */}
        {showSetup && (
          <div className="mb-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-lg" data-testid="setup-panel">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Configure Space Grid</h2>
            <p className="text-sm text-slate-500 mb-6">Set up rows and columns to create your space map. Each cell represents one bookable unit.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Rows</Label>
                <Input type="number" min={1} max={26} value={setupRows} onChange={(e) => setSetupRows(parseInt(e.target.value) || 1)}
                  className="mt-2 h-12 rounded-xl" data-testid="setup-rows" />
              </div>
              <div>
                <Label>Columns</Label>
                <Input type="number" min={1} max={50} value={setupCols} onChange={(e) => setSetupCols(parseInt(e.target.value) || 1)}
                  className="mt-2 h-12 rounded-xl" data-testid="setup-cols" />
              </div>
              <div>
                <Label>Unit Type</Label>
                <Select value={setupType} onValueChange={setSetupType}>
                  <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="setup-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-4">This will create <strong>{setupRows * setupCols}</strong> units. Existing units will be replaced.</p>
            <div className="flex gap-3">
              <Button onClick={handleSetup} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl" data-testid="setup-confirm-btn">
                {saving ? 'Creating...' : 'Create Grid'}
              </Button>
              {units.length > 0 && (
                <Button variant="outline" onClick={() => setShowSetup(false)} className="rounded-xl">Cancel</Button>
              )}
            </div>
          </div>
        )}

        {units.length > 0 && !showSetup && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 border border-slate-200 text-center" data-testid="summary-total">
                <p className="text-2xl font-bold text-slate-900">{summary.total}</p>
                <p className="text-xs text-slate-500 mt-1">Total</p>
              </div>
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <div key={status} className="bg-white rounded-xl p-4 border border-slate-200 text-center" data-testid={`summary-${status}`}>
                  <p className="text-2xl font-bold text-slate-900">{summary[status] || 0}</p>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <div className={`w-2.5 h-2.5 rounded-full ${config.color}`}></div>
                    <p className="text-xs text-slate-500">{config.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Controls Bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Paint mode:</span>
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <button key={status} onClick={() => setPaintMode(paintMode === status ? null : status)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      paintMode === status ? `${config.color} ${config.text} border-transparent shadow-md` : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`paint-${status}`}>
                    <div className={`w-3 h-3 rounded-full ${paintMode === status ? 'bg-white/50' : config.color}`}></div>
                    {config.label}
                  </button>
                ))}
                {paintMode && (
                  <button onClick={() => setPaintMode(null)} className="text-sm text-red-500 hover:text-red-600 font-medium ml-2">
                    <X size={16} weight="bold" className="inline mr-1" /> Clear
                  </button>
                )}
              </div>

              {/* Bulk Update */}
              {selectedUnits.size > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <span className="text-sm text-slate-600">{selectedUnits.size} selected</span>
                  <Select value={bulkStatus} onValueChange={setBulkStatus}>
                    <SelectTrigger className="w-40 h-9 rounded-lg" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([s, c]) => <SelectItem key={s} value={s}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={handleBulkUpdate} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 rounded-lg" data-testid="bulk-update-btn">
                    Apply
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedUnits(new Set())} className="text-slate-500">Clear</Button>
                </div>
              )}
            </div>

            {/* Space Grid */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 overflow-x-auto" data-testid="space-grid">
              <div className="inline-block min-w-fit">
                {/* Column headers */}
                <div className="flex items-center gap-1.5 mb-2 pl-10">
                  {grid.cols && Array.from({ length: grid.cols }, (_, i) => (
                    <div key={i} className="w-14 h-6 flex items-center justify-center text-xs font-medium text-slate-400">
                      {i + 1}
                    </div>
                  ))}
                </div>

                {/* Rows */}
                {Object.entries(rows).sort(([a], [b]) => a - b).map(([rowIdx, rowUnits]) => (
                  <div key={rowIdx} className="flex items-center gap-1.5 mb-1.5">
                    {/* Row label */}
                    <div className="w-8 h-14 flex items-center justify-center text-xs font-semibold text-slate-400">
                      {String.fromCharCode(65 + parseInt(rowIdx))}
                    </div>

                    {/* Unit cells */}
                    {rowUnits.sort((a, b) => a.col - b.col).map(unit => {
                      const cfg = STATUS_CONFIG[unit.status] || STATUS_CONFIG.available;
                      const isSelected = selectedUnits.has(unit.unit_id);
                      return (
                        <button key={unit.unit_id}
                          onClick={(e) => e.shiftKey ? toggleSelect(unit.unit_id) : toggleUnit(unit)}
                          onContextMenu={(e) => { e.preventDefault(); toggleSelect(unit.unit_id); }}
                          className={`w-14 h-14 rounded-xl ${cfg.color} ${cfg.hoverColor} ${cfg.text} flex flex-col items-center justify-center transition-all cursor-pointer relative group ${
                            isSelected ? 'ring-3 ring-blue-500 ring-offset-2' : ''
                          } ${paintMode ? 'cursor-crosshair' : ''}`}
                          data-testid={`unit-${unit.unit_id}`}
                          title={`${unit.label} - ${cfg.label}\nClick to toggle | Shift+Click to select | Right-click to select`}
                        >
                          <span className="text-xs font-bold leading-none">{unit.label}</span>
                          <span className="text-[10px] opacity-70 leading-none mt-0.5">{unit.unit_type}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="font-medium">Legend:</span>
              {Object.entries(STATUS_CONFIG).map(([s, c]) => (
                <span key={s} className="flex items-center gap-1.5">
                  <div className={`w-3.5 h-3.5 rounded ${c.color}`}></div> {c.label}
                </span>
              ))}
              <span className="text-slate-400 ml-4">Click = toggle | Shift+Click = multi-select | Paint = click to apply</span>
            </div>
          </>
        )}

        {units.length === 0 && !showSetup && (
          <div className="text-center py-16">
            <GridFour size={56} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No space grid configured</h3>
            <p className="text-slate-500 mb-6">Set up rows and columns to create your visual space map</p>
            <Button onClick={() => setShowSetup(true)} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl" data-testid="setup-btn">
              <GridFour size={20} className="mr-2" /> Configure Space Grid
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaceManager;
